import React, { useEffect, useMemo, useState } from "react";
import FarmerLayout from "../../components/layouts/FarmerLayout";
import { auth, database, firestore } from "../../firebaseConfig";
import { ref, onValue } from "firebase/database";
import { doc, getDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Droplets,
  Wallet,
  ArrowUpRight,
  Share2,
  ShieldCheck,
  Leaf,
  LineChart,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const CHART_CHECKPOINTS = [
  { label: "Mon", milkScale: 0.28, payScale: 0.18 },
  { label: "Wed", milkScale: 0.42, payScale: 0.26 },
  { label: "Fri", milkScale: 0.6, payScale: 0.34 },
  { label: "Sun", milkScale: 0.78, payScale: 0.42 },
];
const BASE_PAY_FLOOR = [500, 750, 1100, 1400];
const BASE_PAY_MIN = 200;
const DEFAULT_PAY_FLOOR = 800;
const CHART_MARGINS = { top: 10, right: 16, left: 0, bottom: 0 };
const INVITE_FEEDBACK_DURATION = 2400;
const HERO_CARD_CLASS =
  "relative overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-900 via-green-600 to-lime-400 text-white p-6 shadow-xl";
const AI_SCORE_WEIGHTS = {
  milkWeight: 1.8,
  pendingWeight: 3.2,
  earningsDivisor: 600,
  balanceBonus: 0.08,
  floor: 12,
};

const FarmersDashboard = () => {
  const [farmerInfo, setFarmerInfo] = useState(null);
  const [stats, setStats] = useState({
    todayMilk: 0,
    monthMilk: 0,
    paymentsTotal: 0,
    pending: 0,
    balance: 0,
  });

  const [copied, setCopied] = useState(false);

  const user = auth.currentUser;

  const chartData = useMemo(() => {
    return CHART_CHECKPOINTS.map((checkpoint, index) => {
      const scaledMilk = Math.round(stats.monthMilk * checkpoint.milkScale);
      const scaledPay = Number(stats.paymentsTotal) * checkpoint.payScale;

      return {
        label: checkpoint.label,
        milk: Math.max(1, Number.isFinite(scaledMilk) ? scaledMilk : index + 2),
        pay: Math.max(
          BASE_PAY_MIN,
          Number.isFinite(scaledPay)
            ? scaledPay
            : BASE_PAY_FLOOR[index] ?? DEFAULT_PAY_FLOOR
        ),
      };
    });
  }, [stats.monthMilk, stats.paymentsTotal]);

  const aiMomentumScore = useMemo(() => {
    return Math.min(
      100,
      Math.max(
        AI_SCORE_WEIGHTS.floor,
        Math.round(
          stats.monthMilk * AI_SCORE_WEIGHTS.milkWeight +
            stats.pending * AI_SCORE_WEIGHTS.pendingWeight +
            Number(stats.paymentsTotal || 0) / AI_SCORE_WEIGHTS.earningsDivisor +
            Number(stats.balance || 0) * AI_SCORE_WEIGHTS.balanceBonus
        )
      )
    );
  }, [stats.balance, stats.monthMilk, stats.paymentsTotal, stats.pending]);

  const handleCopyInvite = async () => {
    const referral = farmerInfo?.otherName || farmerInfo?.username || "farmer";
    const appOrigin =
      typeof window !== "undefined"
        ? "https://neuracraft-378a2.web.app/"
        : process.env.REACT_APP_API_URL ||
          process.env.REACT_APP_PUBLIC_URL ||
          "https://neuracraft-378a2.web.app/";
    const link = `${appOrigin}`;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(
          `${link}`
        );
        setCopied(true);
        setTimeout(() => setCopied(false), INVITE_FEEDBACK_DURATION);
      } else {
        alert(`Invite link: ${link}`);
      }
    } catch (error) {
      console.error("Unable to copy invite link", error);
      alert(`Invite link: ${link}`);
    }
  };

  useEffect(() => {
    if (!user) return;

    const loadFarmer = async () => {
      // 1️⃣ Load farmer Firestore profile (from users collection)
      const userDoc = await getDoc(doc(firestore, "users", user.uid));
      if (!userDoc.exists()) return;

      const data = userDoc.data();
      setFarmerInfo(data);

      const companyName = data.companyName;
      const farmerName = data.otherName || data.username;

      if (!companyName || !farmerName) return;

      // 2️⃣ Paths to Realtime DB (matching mobile structure)
      const statsRef = ref(
        database,
        `Companies/${companyName}/DashboardStats/${farmerName}`
      );

      onValue(statsRef, (snap) => {
        if (snap.exists()) {
          setStats(snap.val());
        }
      });
    };

    loadFarmer();
  }, [user]);

  return (
    <FarmerLayout>
      <div className="p-6 space-y-8 bg-gradient-to-b from-green-50 via-white to-white rounded-3xl">
        {/* Hero */}
        <div className={HERO_CARD_CLASS}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.18),_transparent_45%)]" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-3 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-semibold">
                <Sparkles className="w-4 h-4" />
                AI Field Briefing
              </div>
              <h1 className="text-3xl md:text-4xl font-bold">
                Welcome back, {farmerInfo?.firstName || "Farmer"}!
              </h1>
              <p className="text-white/85 text-sm md:text-base leading-relaxed">
                Track milk deliveries, advance balances, and payouts in one AI-inspired cockpit.
                Insights come from your FarmerPayments and FarmerMilkRecords to keep retention high.
              </p>
              <div className="flex flex-wrap gap-3">
                <PulsePill label="Milk today" value={`${stats.todayMilk} L`} />
                <PulsePill label="Pending" value={stats.pending} />
                <PulsePill label="Earnings" value={`KES ${stats.paymentsTotal}`} />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/30 shadow-lg max-w-xs w-full">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5" />
                  <span className="text-sm font-semibold">AI Momentum</span>
                </div>
                <span className="text-xs bg-black/30 px-2 py-1 rounded-full">Live</span>
              </div>
              <div className="text-4xl font-bold leading-tight">{aiMomentumScore}%</div>
              <p className="text-xs text-white/80 mt-2">
                Blended confidence from milk throughput, pending payouts, and monthly earnings.
              </p>
              <div className="flex items-center gap-2 text-xs text-white/90 mt-3">
                <ArrowUpRight className="w-4 h-4" />
                Deliver daily and clear pending to boost trust.
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            label="Today's Milk"
            value={`${stats.todayMilk} L`}
            color="from-emerald-100 via-white to-emerald-50"
            icon={<Droplets className="w-5 h-5 text-emerald-700" />}
            hint="Fresh deliveries logged"
          />

          <SummaryCard
            label="This Month"
            value={`${stats.monthMilk} L`}
            color="from-blue-100 via-white to-blue-50"
            icon={<LineChart className="w-5 h-5 text-blue-700" />}
            hint="Cumulative milk records"
          />

          <SummaryCard
            label="Total Earnings"
            value={`KES ${stats.paymentsTotal}`}
            color="from-amber-100 via-white to-amber-50"
            icon={<Wallet className="w-5 h-5 text-amber-700" />}
            hint="From FarmerPayments"
          />

          <SummaryCard
            label="Balance"
            value={`KES ${stats.balance}`}
            color="from-purple-100 via-white to-purple-50"
            icon={<ShieldCheck className="w-5 h-5 text-purple-700" />}
            hint="Advance + realtime"
          />
        </div>

        {/* AI Canvas */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-xs text-gray-500 font-semibold">AI Canvas</p>
                <h3 className="text-lg font-bold text-gray-900">Milk & payout rhythm</h3>
                <p className="text-sm text-gray-600">
                  Inspired by FarmerMilkRecords volume and FarmerPayments payouts.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                <Sparkles className="w-4 h-4" />
                Predictive glow
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={CHART_MARGINS}>
                  <defs>
                    <linearGradient id="colorMilk" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorPay" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="milk"
                    stroke="#10b981"
                    fillOpacity={1}
                    fill="url(#colorMilk)"
                    name="Milk (L)"
                  />
                  <Area
                    type="monotone"
                    dataKey="pay"
                    stroke="#6366f1"
                    fillOpacity={1}
                    fill="url(#colorPay)"
                    name="Payouts"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Leaf className="w-5 h-5 text-emerald-600" />
                <p className="text-sm font-semibold text-gray-800">AI signals</p>
              </div>
              <InsightRow
                title="Throughput"
                value="Keep milk flowing"
                helper={`${stats.monthMilk} L recorded this month`}
              />
              <InsightRow
                title="Payout focus"
                value="Clear pending"
                helper={`${stats.pending} payments in review`}
              />
              <InsightRow
                title="Earnings"
                value="Track totals"
                helper={`Total earnings: KES ${stats.paymentsTotal}`}
              />
            </div>

            <div className="bg-gradient-to-r from-emerald-500 via-green-600 to-lime-500 text-white rounded-2xl p-5 shadow-xl">
              <div className="flex items-center gap-2 mb-2">
                <Share2 className="w-5 h-5" />
                <p className="text-sm font-semibold">Invite farmers</p>
              </div>
              <p className="text-sm text-white/90">
                Bring your fellow farmers to Neuracraft for launch day and keep retention high with shared insights.
              </p>
              <button
                onClick={handleCopyInvite}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-black/20 hover:bg-black/30 rounded-lg text-sm font-semibold transition"
              >
                {copied ? "Link copied" : "Copy invite link"}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="mt-4 bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Quick Navigation</h2>
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-500" />
              Explore faster
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <QuickButton link="/farmer/milk-records" title="Milk Records" icon="🥛" />
            <QuickButton link="/farmer/payments" title="Payments" icon="💵" />
            <QuickButton link="/farmer/ai-assistant" title="AI Assistant" icon="🤖" />
            <QuickButton link="/farmer/settings" title="Settings" icon="⚙️" />
          </div>
        </div>

        {/* Latest Activity */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ActivityItem
            title="Milk Delivered"
            value={`${stats.todayMilk} L delivered today`}
            icon={<Droplets className="w-6 h-6 text-emerald-600" />}
          />

          <ActivityItem
            title="Pending Payments"
            value={`${stats.pending} awaiting approval`}
            icon={<ShieldCheck className="w-6 h-6 text-blue-600" />}
          />

          <ActivityItem
            title="Available Balance"
            value={`KES ${stats.balance}`}
            icon={<Wallet className="w-6 h-6 text-purple-600" />}
          />
        </div>
      </div>
    </FarmerLayout>
  );
};

// COMPONENTS
const SummaryCard = ({ label, value, color, icon, hint }) => (
  <div className={`rounded-2xl p-4 shadow-lg bg-gradient-to-br ${color} border border-gray-100`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs text-gray-600 font-semibold">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {hint ? <p className="text-xs text-gray-500 mt-1">{hint}</p> : null}
      </div>
      <div className="p-2 bg-white rounded-full shadow-inner border border-gray-100">
        {icon}
      </div>
    </div>
  </div>
);

const QuickButton = ({ title, link, icon }) => (
  <Link
    to={link}
    className="bg-white rounded-xl shadow p-4 flex flex-col items-center hover:shadow-md hover:-translate-y-1 transition border border-gray-100"
  >
    <div className="text-3xl">{icon}</div>
    <p className="text-sm font-medium mt-2">{title}</p>
  </Link>
);

const ActivityItem = ({ title, value, icon }) => (
  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 flex items-center space-x-4">
    <div className="text-2xl flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50">
      {icon}
    </div>
    <div>
      <p className="font-semibold text-gray-800">{title}</p>
      <p className="text-sm text-gray-600">{value}</p>
    </div>
  </div>
);

const PulsePill = ({ label, value }) => (
  <div className="px-4 py-2 bg-white/15 backdrop-blur rounded-full text-sm font-semibold border border-white/30">
    <span className="text-white/80">{label}</span>{" "}
    <span className="text-white">{value}</span>
  </div>
);

const InsightRow = ({ title, value, helper }) => (
  <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
    <div className="flex items-center justify-between">
      <p className="text-sm font-semibold text-gray-800">{title}</p>
      <span className="text-xs text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
        {value}
      </span>
    </div>
    <p className="text-xs text-gray-600 mt-1">{helper}</p>
  </div>
);

export default FarmersDashboard;
